import { useState, type FormEvent, type ReactNode } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  DataTable,
  Field,
  Input,
  type ColumnDef,
} from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { CampaignSpend } from '../types';
import { campaignWord } from '../funnel/funnel-words';
import '../admin.css';

export interface SpendFormProps {
  /** What has been entered for the period's months. */
  entries: CampaignSpend[];
  /** The campaigns seen in the period, offered as suggestions. */
  known: readonly { source: string; campaign: string }[];
  saving: boolean;
  error?: string | undefined;
  onEnter: (entry: CampaignSpend) => void;
}

/** "2026-09" from the month input as the first of the month. */
export function monthToDate(month: string): string {
  return `${month}-01`;
}

function thisMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function columnsFor(f: Formatter): ColumnDef<CampaignSpend, unknown>[] {
  return [
    { accessorKey: 'month', header: 'Month', cell: (c) => c.row.original.month.slice(0, 7) },
    { id: 'campaign', header: 'Campaign', accessorFn: (r) => campaignWord(r.source, r.campaign) },
    { accessorKey: 'amount', header: 'Spent', cell: (c) => f.moneyWhole(c.row.original.amount) },
  ];
}

function Suggestions({ id, values }: { id: string; values: string[] }): ReactNode {
  return (
    <datalist id={id}>
      {[...new Set(values)].map((v) => (
        <option key={v} value={v} />
      ))}
    </datalist>
  );
}

interface Draft {
  source: string;
  campaign: string;
  month: string;
  amount: string;
}

function isReady(d: Draft): boolean {
  return (
    d.source.trim() !== '' &&
    d.campaign.trim() !== '' &&
    d.month !== '' &&
    d.amount !== '' &&
    Number(d.amount) >= 0
  );
}

function toEntry(d: Draft): CampaignSpend {
  return {
    source: d.source.trim(),
    campaign: d.campaign.trim(),
    month: monthToDate(d.month),
    amount: Number(d.amount),
  };
}

const emptyDraft = (): Draft => ({ source: '', campaign: '', month: thisMonth(), amount: '' });

interface TextFieldProps {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  value: string;
  onChange: (value: string) => void;
  /** Attributes on the input: type, list, min, step. */
  input?: Record<string, string | number> | undefined;
}

function TextField(p: TextFieldProps): ReactNode {
  return (
    <Field label={p.label} hint={p.hint} error={p.error}>
      {({ id }) => (
        <Input
          id={id}
          {...p.input}
          value={p.value}
          onChange={(e) => p.onChange(e.currentTarget.value)}
          required
        />
      )}
    </Field>
  );
}

interface FieldsProps {
  draft: Draft;
  known: SpendFormProps['known'];
  error: string | undefined;
  set: (patch: Partial<Draft>) => void;
}

function Fields({ draft, known, error, set }: FieldsProps): ReactNode {
  return (
    <>
      <TextField
        label="Source"
        hint="where the campaign ran"
        value={draft.source}
        onChange={(source) => set({ source })}
        input={{ list: 'ad-spend-sources' }}
      />
      <TextField
        label="Campaign"
        value={draft.campaign}
        onChange={(campaign) => set({ campaign })}
        input={{ list: 'ad-spend-campaigns' }}
      />
      <TextField
        label="Month"
        value={draft.month}
        onChange={(month) => set({ month })}
        input={{ type: 'month' }}
      />
      <TextField
        label="Spent"
        hint="in dollars, for that month"
        error={error}
        value={draft.amount}
        onChange={(amount) => set({ amount })}
        input={{ type: 'number', min: 0, step: '0.01', inputMode: 'decimal' }}
      />
      <Suggestions id="ad-spend-sources" values={known.map((k) => k.source)} />
      <Suggestions id="ad-spend-campaigns" values={known.map((k) => k.campaign)} />
    </>
  );
}

function EntriesTable({ entries }: { entries: CampaignSpend[] }): ReactNode {
  const f = useFormat();
  return (
    <DataTable
      columns={columnsFor(f)}
      data={entries}
      dense
      caption="Spend entered by month and campaign"
      empty="Nothing entered for these months yet."
    />
  );
}

/** Staff type in what a campaign cost in a month; the bars above reprice a paying customer. */
export function SpendForm({ entries, known, saving, error, onEnter }: SpendFormProps): ReactNode {
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const ready = isReady(draft);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    onEnter(toEntry(draft));
    setDraft((d) => ({ ...d, amount: '' }));
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>What marketing spent</CardTitle>
      </CardHeader>
      <form className="ad-spend" onSubmit={submit}>
        <Fields
          draft={draft}
          known={known}
          error={error}
          set={(patch) => setDraft((d) => ({ ...d, ...patch }))}
        />
        <div className="ad-spend__submit">
          <Button type="submit" disabled={!ready || saving} loading={saving}>
            Enter spend
          </Button>
        </div>
      </form>
      <EntriesTable entries={entries} />
    </Card>
  );
}
